import React, { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';

/**
 * VoiceWaveform Component
 * Displays a waveform visualization for audio playback
 * Used to show when AI voice is speaking
 * 
 * Memoized to prevent unnecessary re-renders during playback
 */
const VoiceWaveform = React.memo(function VoiceWaveform({ audioUrl, isPlaying, onFinish, onError, height = 60, waveColor = '#D702F6', progressColor = '#FF3B30' }) {
  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const isLoadingRef = useRef(false);
  const isMountedRef = useRef(true);
  const audioContextUnlockedRef = useRef(false);
  const isInitializedRef = useRef(false); // Track initialization to prevent multiple initializations

  // Unlock audio context on user interaction (required for browser auto-play policies)
  useEffect(() => {
    const unlockAudioContext = async () => {
      if (audioContextUnlockedRef.current) return;
      
      try {
        // Try to unlock audio context by creating a temporary audio element and playing it
        const audio = new Audio();
        audio.src = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIGWi77+efTRAMUKfj8LZjHAY4kdfyzHksBSR3x/DdkEAKFF606euoVRQKRp/g8r5sIQUrgc7y2Yk2CBlou+/nn00QDFCn4/C2YxwGOJHX8sx5LAUkd8fw3ZBAC';
        audio.volume = 0.01; // Very quiet, just to unlock
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          await playPromise;
          audio.pause();
          audioContextUnlockedRef.current = true;
          console.log('[WAVEFORM] ✅ Audio context unlocked');
        }
      } catch (error) {
        // Ignore errors - audio context will be unlocked when user interacts
        console.log('[WAVEFORM] Audio context unlock attempt:', error.name);
      }
    };

    // Unlock on any user interaction
    const events = ['click', 'touchstart', 'keydown'];
    const handlers = events.map(event => {
      const handler = () => {
        unlockAudioContext();
        events.forEach(e => document.removeEventListener(e, handlers[events.indexOf(e)]));
      };
      document.addEventListener(event, handler, { once: true });
      return handler;
    });

    return () => {
      events.forEach((event, index) => {
        document.removeEventListener(event, handlers[index]);
      });
    };
  }, []);

  useEffect(() => {
    if (!waveformRef.current) return;
    
    // Prevent multiple initializations
    if (isInitializedRef.current) {
      console.log('[WAVEFORM] Already initialized, skipping re-initialization');
      return;
    }
    
    isMountedRef.current = true;
    isInitializedRef.current = true;

    // Initialize WaveSurfer with audio output configuration
    const wavesurfer = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: waveColor,
      progressColor: progressColor,
      cursorColor: 'transparent',
      barWidth: 2,
      barRadius: 1,
      height: height,
      normalize: true,
      backend: 'WebAudio',
      mediaControls: false,
      interact: false, // Disable interaction for visual-only display
      // CRITICAL: Ensure audio is connected to destination
      audioContext: null, // Let WaveSurfer create its own
      closeAudioContext: false,
      // Enable auto-play (will be handled manually after user interaction)
      autoplay: false, // We'll manually play after interaction
      // Ensure audio output
      splitChannels: false,
    });

    wavesurferRef.current = wavesurfer;

    // Handle ready event - attempt to play if isPlaying is true
    wavesurfer.on('ready', () => {
      console.log('[WAVEFORM] Audio ready, duration:', wavesurfer.getDuration());
      if (isMountedRef.current) {
        isLoadingRef.current = false;
        setIsReady(true);
        
        // Ensure audio context is resumed (required for browser auto-play)
        try {
          const backend = wavesurfer.backend;
          if (backend && backend.ac && backend.ac.state === 'suspended') {
            console.log('[WAVEFORM] Audio context suspended, attempting to resume...');
            backend.ac.resume().then(() => {
              console.log('[WAVEFORM] ✅ Audio context resumed');
            }).catch(err => {
              console.warn('[WAVEFORM] Could not resume audio context:', err);
            });
          }
        } catch (e) {
          // Ignore errors accessing audio context
        }
        
        // If isPlaying is true, try to play immediately
        // This handles the case where audio loads after isPlaying is set
        if (isPlaying) {
          console.log('[WAVEFORM] Audio ready and isPlaying=true, attempting play...');
          const playPromise = wavesurfer.play();
          
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                console.log('[WAVEFORM] ✅ Audio playing successfully');
              })
              .catch((error) => {
                console.error('[WAVEFORM] ❌ Auto-play blocked:', error);
                console.log('[WAVEFORM] Auto-play was blocked by browser. Audio will play after user interaction.');
                // The play will be retried when isPlaying changes in the other useEffect
              });
          }
        }
      }
    });

    // Handle finish event
    wavesurfer.on('finish', () => {
      console.log('🎵 WaveSurfer finish event triggered');
      console.log('🎵 onFinish callback exists:', !!onFinish);
      console.log('🎵 isMounted:', isMountedRef.current);
      
      // Clear progress check interval if it exists
      if (wavesurferRef.current._progressCheck) {
        clearInterval(wavesurferRef.current._progressCheck);
        delete wavesurferRef.current._progressCheck;
        console.log('🎵 Cleared progress check interval');
      }
      
      // Ensure WaveSurfer is stopped
      try {
        if (wavesurferRef.current && wavesurferRef.current.isPlaying()) {
          wavesurferRef.current.stop();
          console.log('🎵 Stopped WaveSurfer playback');
        }
      } catch (error) {
        console.warn('⚠️ Error stopping WaveSurfer:', error);
      }
      
      if (isMountedRef.current && onFinish) {
        console.log('🎵 Calling onFinish callback...');
        try {
          onFinish();
          console.log('✅ onFinish callback executed successfully');
        } catch (error) {
          console.error('❌ Error in onFinish callback:', error);
        }
      } else {
        console.warn('⚠️ onFinish not called - isMounted:', isMountedRef.current, 'onFinish exists:', !!onFinish);
      }
    });

    // Also listen to the underlying HTML audio element's ended event as a backup
    // This ensures we catch when audio finishes even if WaveSurfer's finish event doesn't fire
    const mediaElement = wavesurfer.getMediaElement();
    if (mediaElement) {
      const handleEnded = () => {
        console.log('🎵 HTML audio element ended event triggered');
        
        // Clear progress check interval if it exists
        if (wavesurferRef.current._progressCheck) {
          clearInterval(wavesurferRef.current._progressCheck);
          delete wavesurferRef.current._progressCheck;
          console.log('🎵 Cleared progress check interval (from ended event)');
        }
        
        // Ensure WaveSurfer is stopped
        try {
          if (wavesurferRef.current && wavesurferRef.current.isPlaying()) {
            wavesurferRef.current.stop();
            console.log('🎵 Stopped WaveSurfer playback (from ended event)');
          }
        } catch (error) {
          console.warn('⚠️ Error stopping WaveSurfer (from ended event):', error);
        }
        
        if (isMountedRef.current && onFinish) {
          onFinish();
        }
      };
      mediaElement.addEventListener('ended', handleEnded);
      
      // Store cleanup function
      wavesurferRef.current._endedHandler = handleEnded;
    }

    // Handle errors - suppress abort errors during cleanup
    wavesurfer.on('error', (error) => {
      // AbortError is expected when component unmounts or URL changes
      if (error.name === 'AbortError' || error.message?.includes('aborted')) {
        // Silently ignore - this is expected behavior
        isLoadingRef.current = false;
        return;
      }
      console.error('WaveSurfer error:', error);
      isLoadingRef.current = false;
      // Call onError callback if provided
      if (isMountedRef.current && onError) {
        onError();
      }
    });

    // Cleanup
    return () => {
      isMountedRef.current = false;
      isLoadingRef.current = false;
      isInitializedRef.current = false;
      
      if (wavesurfer) {
        try {
          // Remove ended event listener if it exists
          const mediaElement = wavesurfer.getMediaElement();
          if (mediaElement && wavesurfer._endedHandler) {
            mediaElement.removeEventListener('ended', wavesurfer._endedHandler);
            delete wavesurfer._endedHandler;
          }
          
          // Stop any playback before destroying
          if (wavesurfer.isPlaying()) {
            wavesurfer.pause();
          }
          // Don't try to unload - just destroy cleanly
          // The abort error will be caught by the error handler
          wavesurfer.destroy();
        } catch (error) {
          // Ignore cleanup errors - component is unmounting anyway
        }
      }
    };
  }, []); // Empty deps - only initialize once

  // Load audio when URL changes
  useEffect(() => {
    if (!audioUrl || !wavesurferRef.current || !isMountedRef.current) return;
    
    // Check if already loaded
    const mediaElement = wavesurferRef.current.getMediaElement();
    const currentSrc = mediaElement?.src;
    if (currentSrc === audioUrl || currentSrc === window.location.href + audioUrl) {
      console.log('WaveSurfer: Audio URL already loaded, skipping');
      return;
    }
    
    // Prevent loading if already loading
    if (isLoadingRef.current) {
      console.log('WaveSurfer: Already loading, skipping');
      return;
    }
    
    isLoadingRef.current = true;
    setIsReady(false);
    
    console.log('WaveSurfer: Loading audio URL:', audioUrl.substring(0, 50) + '...');
    
    // Use setTimeout to ensure this happens after any cleanup
    const loadTimeout = setTimeout(() => {
      if (!isMountedRef.current || !wavesurferRef.current) {
        isLoadingRef.current = false;
        return;
      }
      
      try {
        const loadPromise = wavesurferRef.current.load(audioUrl);
        
        // Set up ended event listener after a short delay to ensure media element is ready
        const setupEndedListener = () => {
          setTimeout(() => {
            if (!isMountedRef.current || !wavesurferRef.current) return;
            const newMediaElement = wavesurferRef.current.getMediaElement();
            if (newMediaElement && onFinish) {
              // Remove old listener if it exists
              if (wavesurferRef.current._endedHandler) {
                newMediaElement.removeEventListener('ended', wavesurferRef.current._endedHandler);
              }
              // Add new listener
              const handleEnded = () => {
                console.log('🎵 HTML audio element ended event triggered (after load)');
                console.log('🎵 Media element currentTime:', newMediaElement.currentTime, 'duration:', newMediaElement.duration);
                console.log('🎵 onFinish callback exists:', !!onFinish);
                console.log('🎵 isMounted:', isMountedRef.current);
                if (isMountedRef.current && onFinish) {
                  console.log('🎵 Calling onFinish callback from ended event...');
                  try {
                    onFinish();
                    console.log('✅ onFinish callback executed successfully from ended event');
                  } catch (error) {
                    console.error('❌ Error in onFinish callback:', error);
                  }
                } else {
                  console.warn('⚠️ onFinish not called from ended event - isMounted:', isMountedRef.current, 'onFinish exists:', !!onFinish);
                }
              };
              newMediaElement.addEventListener('ended', handleEnded);
              wavesurferRef.current._endedHandler = handleEnded;
              console.log('✅ Ended event listener attached to media element');
            }
          }, 200);
        };
        
        if (loadPromise && typeof loadPromise.then === 'function') {
          loadPromise.then(() => {
            setupEndedListener();
          }).catch((error) => {
            // Silently handle abort errors - they're expected during cleanup/URL changes
            if (error.name === 'AbortError' || 
                error.message?.includes('aborted') ||
                error.message?.includes('interrupted')) {
              isLoadingRef.current = false;
              return;
            }
            console.error('WaveSurfer: Error loading audio:', error);
            isLoadingRef.current = false;
          });
        } else {
          // If load() doesn't return a promise, set up listener after a delay
          setupEndedListener();
        }
      } catch (error) {
        // Silently handle abort errors - they're expected
        if (error.name === 'AbortError' || 
            error.message?.includes('aborted') ||
            error.message?.includes('interrupted')) {
          isLoadingRef.current = false;
          return;
        }
        console.error('WaveSurfer: Error loading audio:', error);
        isLoadingRef.current = false;
      }
    }, 0);
    
    return () => {
      clearTimeout(loadTimeout);
    };
  }, [audioUrl, onFinish]);

  // Play/pause based on isPlaying prop
  useEffect(() => {
    if (!wavesurferRef.current || !isMountedRef.current) return;
    
    // Don't try to play/pause if not ready yet
    if (!isReady) {
      console.log('WaveSurfer: Not ready yet, waiting...');
      return;
    }

    // Use setTimeout to avoid race conditions with load()
    const playPauseTimeout = setTimeout(() => {
      if (!isMountedRef.current || !wavesurferRef.current) return;
      
      try {
        if (isPlaying) {
          const mediaElement = wavesurferRef.current.getMediaElement();
          
          // Check if audio is actually playing by checking the media element state
          const isActuallyPlaying = mediaElement && !mediaElement.paused && mediaElement.currentTime > 0 && mediaElement.currentTime < mediaElement.duration;
          const wavesurferSaysPlaying = wavesurferRef.current.isPlaying();
          
          console.log('[WAVEFORM] 🎵 Play attempt check:', {
            isPlayingProp: isPlaying,
            wavesurferSaysPlaying,
            isActuallyPlaying,
            mediaPaused: mediaElement?.paused,
            mediaCurrentTime: mediaElement?.currentTime,
            mediaDuration: mediaElement?.duration
          });
          
          // Only play if not actually playing (check both WaveSurfer state and media element state)
          if (!isActuallyPlaying && !wavesurferSaysPlaying) {
            console.log('[WAVEFORM] 🎵 Starting playback');
            
            if (mediaElement) {
              console.log('[WAVEFORM] Media element duration:', mediaElement.duration, 'seconds');
              console.log('[WAVEFORM] Media element paused:', mediaElement.paused);
              console.log('[WAVEFORM] Media element muted:', mediaElement.muted);
              
              // CRITICAL: Force unmute and set volume BEFORE attempting to play
              // This ensures audio will actually play on player side
              mediaElement.muted = false;
              mediaElement.volume = 1;
              
              console.log('[WAVEFORM] 🔊 Audio settings before play:', {
                muted: mediaElement.muted,
                volume: mediaElement.volume,
                readyState: mediaElement.readyState,
                networkState: mediaElement.networkState,
                paused: mediaElement.paused,
                currentTime: mediaElement.currentTime,
                duration: mediaElement.duration
              });
              
              // Reset to beginning if audio is at the end
              if (mediaElement.duration > 0 && mediaElement.currentTime >= mediaElement.duration - 0.1) {
                console.log('[WAVEFORM] ⚠️ Audio at end, resetting to start');
                mediaElement.currentTime = 0;
              }
              
              // Ensure audio context is resumed (critical for autoplay)
              try {
                const backend = wavesurferRef.current?.backend;
                if (backend?.ac && backend.ac.state === 'suspended') {
                  console.log('[WAVEFORM] ⚠️ Audio context suspended, resuming...');
                  backend.ac.resume().then(() => {
                    console.log('[WAVEFORM] ✅ Audio context resumed');
                  }).catch(err => {
                    console.error('[WAVEFORM] ❌ Could not resume audio context:', err);
                  });
                }
              } catch (e) {
                console.warn('[WAVEFORM] Could not access audio context:', e);
              }
              
              // Monitor playback progress and manually detect when audio finishes
              // This is a backup in case the ended event doesn't fire
              // Use more frequent checks (250ms) to catch finish more accurately
              const checkProgress = setInterval(() => {
                if (!isMountedRef.current || !wavesurferRef.current) {
                  clearInterval(checkProgress);
                  return;
                }
                
                const currentMediaElement = wavesurferRef.current.getMediaElement();
                if (currentMediaElement && !currentMediaElement.paused) {
                  const currentTime = currentMediaElement.currentTime;
                  const duration = currentMediaElement.duration;
                  
                  // Check if audio has finished - be more conservative to avoid cutting off early
                  // Only trigger finish when we're very close to the end (within 0.1s) AND audio is actually finished
                  if (duration > 0) {
                    const timeRemaining = duration - currentTime;
                    // More conservative: only finish if we're within 0.1s AND audio has actually ended
                    // Don't trigger on "near end" - wait for actual finish
                    const isActuallyFinished = currentTime >= duration - 0.05 || 
                      (currentMediaElement.paused && currentTime > 0 && currentTime >= duration - 0.1);
                    
                    // Also check if audio ended naturally (ended event should have fired, but check as backup)
                    const hasReachedEnd = currentTime >= duration - 0.05;
                    const isPausedAtEnd = currentMediaElement.paused && currentTime > 0 && hasReachedEnd;
                    
                    if (isActuallyFinished || isPausedAtEnd) {
                      console.log('[WAVEFORM] 🎵 Audio finished detected manually');
                      console.log('[WAVEFORM] currentTime:', currentTime.toFixed(3), 'duration:', duration.toFixed(3), 'timeRemaining:', timeRemaining.toFixed(3));
                      console.log('[WAVEFORM] isActuallyFinished:', isActuallyFinished, 'isPausedAtEnd:', isPausedAtEnd);
                      clearInterval(checkProgress);
                      delete wavesurferRef.current._progressCheck;
                      
                      // Manually call onFinish if events didn't fire
                      if (isMountedRef.current && onFinish) {
                        console.log('[WAVEFORM] 🎵 Manually calling onFinish callback...');
                        setTimeout(() => {
                          if (isMountedRef.current && onFinish) {
                            try {
                              onFinish();
                              console.log('[WAVEFORM] ✅ onFinish callback executed successfully (manual)');
                            } catch (error) {
                              console.error('[WAVEFORM] ❌ Error in onFinish callback (manual):', error);
                            }
                          }
                        }, 100);
                      }
                      return;
                    }
                  }
                } else if (currentMediaElement?.paused || !currentMediaElement) {
                  // Audio paused or element removed, clean up
                  clearInterval(checkProgress);
                  delete wavesurferRef.current._progressCheck;
                }
              }, 250); // Check every 250ms for more accurate finish detection
              
              // Clean up interval when component unmounts or audio stops
              wavesurferRef.current._progressCheck = checkProgress;
            }
            
            // Attempt to play with proper error handling for auto-play restrictions
            console.log('[WAVEFORM] 🎵 Attempting to play audio...');
            const playPromise = wavesurferRef.current.play();
            
            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                  console.log('[WAVEFORM] ✅ Audio play() promise resolved');
                  // Verify playback after a short delay
                  setTimeout(() => {
                    const mediaEl = wavesurferRef.current?.getMediaElement();
                    if (mediaEl) {
                      console.log('[WAVEFORM] Playback status after play():', {
                        paused: mediaEl.paused,
                        muted: mediaEl.muted,
                        volume: mediaEl.volume,
                        currentTime: mediaEl.currentTime,
                        duration: mediaEl.duration,
                        readyState: mediaEl.readyState,
                        networkState: mediaEl.networkState
                      });
                      
                      // If still paused, try to play again (sometimes play() resolves but doesn't actually play)
                      if (mediaEl.paused && mediaEl.readyState >= 2) {
                        console.log('[WAVEFORM] ⚠️ Audio still paused after play(), attempting direct play...');
                        const directPlayPromise = mediaEl.play();
                        if (directPlayPromise !== undefined) {
                          directPlayPromise
                            .then(() => console.log('[WAVEFORM] ✅ Direct play() succeeded'))
                            .catch((err) => console.error('[WAVEFORM] ❌ Direct play() failed:', err));
                        }
                      }
                    }
                  }, 200);
                })
                .catch((error) => {
                  console.error('[WAVEFORM] ❌ Auto-play blocked:', error);
                  console.error('[WAVEFORM] Error details:', {
                    name: error.name,
                    message: error.message,
                    code: error.code
                  });
                  console.log('[WAVEFORM] ⚠️ Browser auto-play policy blocked audio. User interaction required.');
                  // Call onError callback if provided
                  if (isMountedRef.current && onError) {
                    onError();
                  }
                });
            } else {
              // Fallback for older browsers
              console.log('[WAVEFORM] Play promise not available, using direct play()');
              try {
                wavesurferRef.current.play();
              } catch (error) {
                console.error('[WAVEFORM] Direct play error:', error);
                if (isMountedRef.current && onError) {
                  onError();
                }
              }
            }
          } else {
            console.log('[WAVEFORM] ⏸️ Audio already playing, skipping (this is normal)');
            // If audio is actually playing, that's fine - don't interrupt it
            // The issue was that we were stopping it unnecessarily
          }
        } else {
          // Only pause if currently playing
          if (wavesurferRef.current.isPlaying()) {
            console.log('WaveSurfer: Pausing playback');
            // Clear progress check interval if it exists
            if (wavesurferRef.current._progressCheck) {
              clearInterval(wavesurferRef.current._progressCheck);
              delete wavesurferRef.current._progressCheck;
            }
            wavesurferRef.current.pause();
          }
        }
      } catch (error) {
        // Suppress play/pause errors during rapid state changes
        if (error.name === 'AbortError' || 
            error.message?.includes('interrupted') ||
            error.message?.includes('pause')) {
          // Expected during rapid state changes
          return;
        }
        console.error('WaveSurfer: Play/pause error:', error);
      }
    }, 100); // Small delay to let load() complete
    
    return () => {
      clearTimeout(playPauseTimeout);
      // Clean up progress check interval
      if (wavesurferRef.current?._progressCheck) {
        clearInterval(wavesurferRef.current._progressCheck);
        delete wavesurferRef.current._progressCheck;
      }
    };
  }, [isPlaying, isReady]);

  return (
    <div style={{
      width: '100%',
      padding: '0.5rem',
      background: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '8px',
      border: isPlaying ? `1px solid ${waveColor}` : '1px solid rgba(255, 255, 255, 0.1)',
      transition: 'all 0.3s ease',
    }}>
      <div ref={waveformRef} style={{ width: '100%' }} />
      {!isReady && audioUrl && (
        <div style={{
          textAlign: 'center',
          color: 'rgba(255, 255, 255, 0.5)',
          fontSize: '0.75rem',
          padding: '0.5rem',
        }}>
          Loading audio...
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  // Only re-render if audioUrl or isPlaying changes
  // This prevents re-renders when parent component updates but audio props haven't changed
  return (
    prevProps.audioUrl === nextProps.audioUrl &&
    prevProps.isPlaying === nextProps.isPlaying &&
    prevProps.height === nextProps.height &&
    prevProps.waveColor === nextProps.waveColor &&
    prevProps.progressColor === nextProps.progressColor
  );
});

export default VoiceWaveform;

